import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpenIcon, TruckIcon, WarehouseIcon, WrenchIcon, GaugeIcon, PackageIcon, ShieldIcon, BarChart3Icon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Section({ icon: Icon, title, color, children, testId }) {
  return (
    <Card className="bg-[#14151A] border-border rounded-none p-6 mb-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
        <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: color }}>
          <Icon className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <h2 className="font-display text-2xl font-bold">{title}</h2>
      </div>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </Card>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 flex-shrink-0 bg-[#FFD60A] text-black font-bold font-display text-lg flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1">
        <div className="text-white font-semibold text-sm mb-1">{title}</div>
        <div className="text-xs text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

export default function Manual() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#00E5FF] mb-1 flex items-center gap-2">
          <BookOpenIcon className="w-3 h-3" /> Manual do Sistema
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Como usar o CHEM·WMS</h1>
        <div className="text-xs text-muted-foreground mt-2">
          {isAdmin
            ? "Guia rápido para operadores, gestores e administradores. Este documento serve também como apresentação institucional do sistema."
            : "Guia rápido para operadores e gestores."}
        </div>
      </div>

      <Section icon={ShieldIcon} title="1. Perfis de Acesso" color="#00E5FF" testId="manual-perfis">
        <div className={`grid grid-cols-1 gap-3 ${isAdmin ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          <div className="border border-border p-3">
            <Badge className="rounded-none bg-[#34C759] text-black">OPERADOR</Badge>
            <div className="text-xs text-muted-foreground mt-2">Bipagem, envase, triagem, expedição. Mobile-friendly.</div>
          </div>
          <div className="border border-border p-3">
            <Badge className="rounded-none bg-[#00E5FF] text-black">GESTOR</Badge>
            <div className="text-xs text-muted-foreground mt-2">Dashboard estratégico, BI, cria operadores da sua base.</div>
          </div>
          {isAdmin && (
            <div className="border border-border p-3">
              <Badge className="rounded-none bg-[#FFD60A] text-black">ADMIN</Badge>
              <div className="text-xs text-muted-foreground mt-2">Governança global — bases, produtos, clientes, auditoria.</div>
            </div>
          )}
        </div>
        <div className="text-xs mt-3">Usuários criados por Admin ou Gestor recebem <span className="text-[#00E5FF]">senha temporária</span> e devem trocá-la no 1º acesso obrigatoriamente.</div>
      </Section>

      <Section icon={TruckIcon} title="2. Recebimento de Matéria-Prima (Granel)" color="#FFD60A" testId="manual-recebimento">
        <Step n="1" title="Chegada do caminhão-tanque">Operador registra NF, Fornecedor, Lote, Densidade e Peso Bruto. Sistema calcula volume automaticamente.</Step>
        <Step n="2" title="Liberação de laudo">Ao aprovar, a remessa entra em <span className="text-[#00E5FF]">PRONTO_DESCARGA</span> e alimenta o Saldo de Granel do produto.</Step>
      </Section>

      <Section icon={WarehouseIcon} title="3. Triagem de Vazios (Logística Reversa)" color="#FF9F0A" testId="manual-triagem">
        <Step n="1" title="Entrada de vazios">Tanques retornam do cliente ou são importados por planilha Excel ou registrados manualmente. Entram em <span className="text-[#00E5FF]">AGUARDANDO INSPEÇÃO</span>.</Step>
        <Step n="2" title="Inspeção rápida">Operador APROVA (→ Vazio Apto) ou REPROVA informando motivo (→ Manutenção).</Step>
        <Step n="3" title="Manutenção">Checklist de 3 pontos (lavagem/estanqueidade/estrutura) + Histórico de Reparo obrigatório antes de liberar.</Step>
      </Section>

      <Section icon={GaugeIcon} title="4. Linha de Envase" color="#34C759" testId="manual-envase">
        <Step n="1" title="Sugestão FEFO">Sistema sugere contentores vazios aptos por ordem de validade (First-Expire-First-Out).</Step>
        <Step n="2" title="Envase e vinculação">Operador informa quantidade envasada + 3 lacres de segurança. Sistema gera lote automático e calcula validade.</Step>
        <Step n="3" title="Registro auditável">Cada envase carimba usuário, timestamp e associa ao recebimento de granel — abatendo o saldo.</Step>
      </Section>

      <Section icon={PackageIcon} title="5. Expedição" color="#00E5FF" testId="manual-expedicao">
        <Step n="1" title="Seleção em lote">Até 30 contentores por carreta. Vincula pedido, NF, motorista, placa, CPF, cliente.</Step>
        <Step n="2" title="Rastreabilidade">Cada contentor tem um GPS ID monitorado no Dashboard em tempo real.</Step>
        <Step n="3" title="Concluir entrega">No painel de Expedição, botão <span className="text-[#34C759]">Concluir</span> registra timestamp exato de entrega no cliente.</Step>
      </Section>

      <Section icon={WrenchIcon} title="6. Manutenção" color="#FF3B30" testId="manual-manut">
        <p>Contentores reprovados ficam listados com o motivo. O operador deve realizar as ações corretivas e:</p>
        <ul className="list-disc pl-5 space-y-1 text-xs">
          <li>Marcar os 3 itens do checklist técnico</li>
          <li>Preencher o campo <span className="text-white">Histórico de Reparo</span> descrevendo o que foi consertado</li>
          <li>Opcionalmente atualizar validade do próximo teste</li>
          <li>Clicar em <span className="text-[#34C759]">Disponibilizar p/ Envase</span></li>
        </ul>
      </Section>

      <Section icon={BarChart3Icon} title="7. Business Intelligence" color="#FFD60A" testId="manual-bi">
        <p>A tela Relatórios & BI (menu <span className="text-white">Relatórios</span>) oferece visão executiva completa dos últimos 12 meses:</p>
        <ul className="list-disc pl-5 space-y-1 text-xs">
          <li>Volume envasado por mês</li>
          <li>Chegadas de granel × Volume recebido</li>
          <li>Ordens de reparo e reprovações de inspeção</li>
          <li>Top 10 produtos por volume</li>
          <li>Download nativo em <span className="text-[#34C759]">.xlsx</span> e <span className="text-[#FFD60A]">.pptx</span></li>
        </ul>
      </Section>

      <Card className="bg-gradient-to-r from-[#FFD60A]/10 to-[#00E5FF]/10 border-border rounded-none p-6 text-center">
        <h3 className="font-display text-2xl font-bold mb-2">Segurança Química é Governança em Tempo Real.</h3>
        <div className="text-xs text-muted-foreground">CHEM·WMS · Envase Químico · Rastreabilidade Total · Logística Reversa</div>
      </Card>
    </div>
  );
}
